import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuth as useSupabaseAuth } from '../../contexts/AuthContext';
import { useAuth as useAppAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { RECONSENT_GATE_MODE, CONTACT_OPTIN_PROMPT_ENABLED, consentOutdated } from '../../config/consent';
import PassportGapForm from '../Passport/PassportGapForm';
import {
  ExplorerOptInGap,
  explorerNeedsContactOptInNudge,
  getExplorerOptInGaps,
  getPassportNudgeHref,
  mapExplorerPrefsFromApi,
  MappedExplorerPrefs,
} from '../../utils/profilePrefs';

/**
 * One-time nudge with inline Passport gap editing (same GET/PUT as Profile/Passport).
 * Shown once per user via Supabase user_metadata; deferred while re-consent gate is active.
 */

const EXEMPT_PREFIXES = [
  '/auth', '/login', '/signup', '/recruiter-signup', '/signup-choice',
  '/reset-password', '/forgot-password', '/email-confirmation', '/resend-confirmation',
  '/terms', '/privacy', '/imprint', '/cookies', '/legal',
];

function reconsentBlocksNudge(
  mode: typeof RECONSENT_GATE_MODE,
  outdated: boolean,
  isAdmin: boolean | null,
): boolean {
  if (mode === 'off' || !outdated) return false;
  if (mode === 'all') return true;
  if (mode === 'admin') return isAdmin === true;
  return false;
}

const ContactOptInPrompt: React.FC = () => {
  const { user: supabaseUser, loading } = useSupabaseAuth();
  const { user, apiCall, isSignedIn } = useAppAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [missingGaps, setMissingGaps] = useState<ExplorerOptInGap[]>([]);
  const [formInitial, setFormInitial] = useState<MappedExplorerPrefs | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(
    RECONSENT_GATE_MODE === 'admin' ? null : false,
  );

  const de = (i18n.language || '').toLowerCase().startsWith('de');
  const exempt = EXEMPT_PREFIXES.some((p) => location.pathname.startsWith(p));

  const meta = supabaseUser?.user_metadata;
  const alreadyPrompted = !!meta?.contact_prefs_prompted_at;
  const outdated = !!supabaseUser && consentOutdated(meta);
  const reconsentRollout = reconsentBlocksNudge(RECONSENT_GATE_MODE, outdated, isAdmin);

  useEffect(() => {
    let cancelled = false;
    if (RECONSENT_GATE_MODE !== 'admin') return;
    if (loading || !user?.userId || !outdated) {
      setIsAdmin(false);
      return;
    }
    (async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.userId}`);
        if (cancelled) return;
        if (res.ok) {
          const p = await res.json();
          setIsAdmin(p.is_admin === true);
        } else {
          setIsAdmin(false);
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.userId, outdated]);

  const markPrompted = async () => {
    try {
      await supabase.auth.updateUser({ data: { contact_prefs_prompted_at: new Date().toISOString() } });
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    let cancelled = false;
    const evaluate = async () => {
      if (!CONTACT_OPTIN_PROMPT_ENABLED) return;
      if (loading || !isSignedIn || !user?.userId) return;
      if (exempt || alreadyPrompted || reconsentRollout) return;
      if (RECONSENT_GATE_MODE === 'admin' && outdated && isAdmin === null) return;
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const res = await apiCall(`${apiBase}/api/v1/profiles/${user.userId}`);
        if (!res.ok || cancelled) return;
        const p = await res.json();
        const gaps = getExplorerOptInGaps(p);
        if (explorerNeedsContactOptInNudge(p)) {
          if (!cancelled) {
            setMissingGaps(gaps);
            setFormInitial(mapExplorerPrefsFromApi(p));
            setShow(true);
          }
        } else {
          await markPrompted();
        }
      } catch { /* ignore */ }
    };
    evaluate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    isSignedIn,
    user?.userId,
    alreadyPrompted,
    reconsentRollout,
    exempt,
    meta?.terms_version,
    meta?.privacy_version,
    isAdmin,
  ]);

  if (!show || missingGaps.length === 0 || !formInitial || !user?.userId) return null;

  const dismiss = async () => {
    setShow(false);
    await markPrompted();
  };

  const handleSaved = async () => {
    setShow(false);
    await markPrompted();
  };

  const openPassport = async () => {
    await markPrompted();
    setShow(false);
    navigate(getPassportNudgeHref(missingGaps));
  };

  return (
    <div className="fixed bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 z-[10040] sm:max-w-md">
      <div className="bg-white rounded-2xl shadow-2xl border border-pink-100 p-4">
        <div className="flex items-start gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">
              {de ? 'Passport vervollständigen' : 'Complete your Passport'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {de ? 'Noch offen, kurz hier einstellen:' : 'Still open, set it here:'}
            </p>
          </div>
          <button onClick={dismiss} className="text-slate-300 hover:text-slate-500 flex-shrink-0" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>

        <PassportGapForm
          gaps={missingGaps}
          initial={formInitial}
          userId={user.userId}
          apiCall={apiCall}
          onSaved={handleSaved}
          onLater={dismiss}
          onOpenPassport={openPassport}
          laterLabel={de ? 'Später' : 'Not now'}
        />
      </div>
    </div>
  );
};

export default ContactOptInPrompt;
